import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideCheckNoChangesConfig,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { Meta } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { environment } from '@eagle/env';
import {
  featureFlagFactoryInit,
  FeatureFlagService,
} from '@eagle/app/shared/services/feature-flag';
import { SharedModule } from './shared/shared.module';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { DateTimePipe } from './shared/pipes/date-time.pipe';
import { provideDatePicker } from './shared/components/date-time/date-picker.providers';
import { RollbarErrorHandler, RollbarService, rollbarFactory } from './core/rollbar/rollbar';
import { provideI18n } from './core/modules/i18n/i18n.providers';
import { AppUpdateService } from './core/modules/app-update/app-update.service';
import { provideAppUpdate } from './core/modules/app-update/app-update.providers';
import { ROUTES } from './modules/app.routes';
import './core/moment-locales';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // Dev mode only (a no-op in optimized builds): reports bindings that changed without notifying change detection,
    // i.e. views that would stay stale under zoneless change detection
    provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 }),
    provideI18n(),
    provideAppUpdate(),
    provideDatePicker(),
    DecimalPipe,
    DateTimePipe,
    provideRouter(ROUTES, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    importProvidersFrom(
      SharedModule,
      NgxSkeletonLoaderModule.forRoot({ animation: 'progress-dark' })
    ),
    provideEnvironmentInitializer(() => {
      const appUpdateService = inject(AppUpdateService);
      appUpdateService.initUpdates();
      appUpdateService.initPush();
    }),
    provideAppInitializer(() => featureFlagFactoryInit(inject(FeatureFlagService))()),
    provideAppInitializer(() => {
      if (environment.tag !== 'prod') {
        inject(Meta).addTag({ name: 'robots', content: 'noindex, nofollow' });
      }
    }),
  ],
};
